import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const normalizeText = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

const normalizeMatchValue = (value) => {
    try {
        if (value && typeof value === "object") {
            const identity =
                value.id ?? value.value ?? value.text ?? value.label ?? value.name;
            if (identity !== null && identity !== undefined) {
                return normalizeMatchValue(identity);
            }
            return "";
        }
        return normalizeText(value);
    } catch (error) {
        return "";
    }
};

const asArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    if (value && typeof value === "object") {
        for (const key of ["data", "items", "members", "results"]) {
            if (Array.isArray(value[key])) {
                return value[key];
            }
        }
        return [];
    }
    return [];
};

const normalizeCollection = (value) => {
    try {
        if (Array.isArray(value)) {
            return value;
        }
        if (value && typeof value === "object") {
            for (const key of [
                "data",
                "items",
                "members",
                "results",
                "communications",
                "questions",
                "answers",
            ]) {
                if (Array.isArray(value[key])) {
                    return value[key];
                }
            }
        }
        return [];
    } catch (error) {
        return [];
    }
};

const getLocalizedText = (entries, language, fallback = "") => {
    if (typeof entries === "string" || typeof entries === "number") {
        const text = String(entries).trim();
        return text !== "" ? text : fallback;
    }
    if (!entries || typeof entries !== "object") {
        return fallback;
    }
    if (!Array.isArray(entries)) {
        return fallback;
    }
    const normalizedLanguage = String(language ?? "").split("-")[0];
    const findByLang = (lang) =>
        entries.find((entry) => entry?.language === lang)?.text;
    return (
        findByLang(language) ??
        findByLang(normalizedLanguage) ??
        findByLang("es") ??
        entries.find(
            (entry) =>
                typeof entry?.text === "string" && entry.text.trim() !== "",
        )?.text ??
        fallback
    );
};

const getQuestionOptions = (question) => {
    if (!question || typeof question !== "object") {
        return [];
    }
    for (const key of ["options", "questionOptions", "question_options"]) {
        const raw = question[key];
        if (Array.isArray(raw)) {
            return raw;
        }
        if (raw && typeof raw === "object") {
            const wrapped = asArray(raw);
            if (wrapped.length > 0) {
                return wrapped;
            }
        }
    }
    return [];
};

const getQuestionMatchIdentities = (question) => {
    try {
        if (question === null || question === undefined) {
            return new Set();
        }
        if (typeof question !== "object") {
            const normalized = normalizeMatchValue(question);
            return normalized ? new Set([normalized]) : new Set();
        }
        const raw = [question.id, question.alias, question.key];
        const normalized = raw
            .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
            .map((v) => normalizeMatchValue(v))
            .filter((v) => v !== "");
        return new Set(normalized);
    } catch (error) {
        return new Set();
    }
};

const getAnswerCandidateValues = (answer) => {
    try {
        if (!answer || typeof answer !== "object") {
            return new Set();
        }
        const values = [
            answer.answerText,
            answer.answer,
            answer.value,
            answer.text,
        ];
        const selected =
            answer.questionOption ?? answer.question_option ?? answer.option;
        if (selected && typeof selected === "object") {
            values.push(
                selected.id,
                selected.value,
                selected.text,
                selected.label,
                selected.name,
            );
        } else {
            values.push(selected);
        }
        return new Set(
            values
                .filter((v) => {
                    if (v === null || v === undefined) return false;
                    if (typeof v === "object") return false;
                    return String(v).trim() !== "";
                })
                .map((v) => normalizeMatchValue(v))
                .filter((v) => v !== ""),
        );
    } catch (error) {
        return new Set();
    }
};

const getOptionMatchValues = (option) => {
    try {
        if (option === null || option === undefined) {
            return new Set();
        }
        if (typeof option === "string" || typeof option === "number") {
            const normalized = normalizeMatchValue(option);
            return normalized ? new Set([normalized]) : new Set();
        }
        if (typeof option !== "object") {
            return new Set();
        }
        const rawTexts = asArray(option.text ?? option.label ?? option.name).map(
            (entry) => {
                if (typeof entry === "string" || typeof entry === "number") {
                    return entry;
                }
                return entry?.text ?? entry?.label ?? entry?.value;
            },
        );
        return new Set(
            [option.id, option.value, option.key, ...rawTexts]
                .filter((v) => {
                    if (v === null || v === undefined) return false;
                    if (typeof v === "object") return false;
                    return String(v).trim() !== "";
                })
                .map((v) => normalizeMatchValue(v))
                .filter((v) => v !== ""),
        );
    } catch (error) {
        return new Set();
    }
};

const getOptionDisplayName = (option, language, index) => {
    try {
        if (typeof option === "string" || typeof option === "number") {
            const text = String(option).trim();
            return text !== "" ? text : `option-${index}`;
        }
        const fallback = String(
            option?.value ?? option?.id ?? option?.key ?? `option-${index}`,
        );
        return (
            getLocalizedText(
                option?.text ?? option?.label ?? option?.name,
                language,
                fallback,
            ) || fallback
        );
    } catch (error) {
        return `option-${index}`;
    }
};

// --- Detección de preguntas ---

const RELATIONSHIP_ALIAS_PATTERNS = [
    /relaci/,
    /relationship/,
    /relacao/,
    /relation/,
    /vinculo/,
    /ligacao/,
];

const RELATIONSHIP_TEXT_PATTERNS = [
    "relacion con la organizacion",
    "relacao com",
    "relationship with",
    "relation with",
    "votre relation",
];

const ACCUSED_ALIAS_PATTERNS = [
    /denunciando/,
    /denunciado/,
    /involucrad/,
    /involved/,
    /implicad/,
    /categoria_de_empleado/,
    /employee_category/,
    /accused/,
    /reported_person/,
];

const ACCUSED_TEXT_PATTERNS = [
    "denunciando",
    "denunciado",
    "involucrad",
    "involved",
    "categoria de empleado",
    "employee category",
    "personas / cargos",
    "personas involucradas",
];

const ANONYMOUS_ALIAS_PATTERNS = [
    /(^|_)do_you_want_to_remain_anonymous(_|$)/,
    /(^|_)desea_permanecer_en_el_anonimato(_|$)/,
    /(^|_)deseja_permanecer_anonim[oa](_|$)/,
];

const ANONYMOUS_QUESTION_TEXTS = [
    "do you want to remain anonymous",
    "would you like to remain anonymous",
    "desea permanecer en el anonimato",
    "deseja permanecer anonimo",
];

const matchesAnyPattern = (normalizedValue, aliasPatterns, textPatterns) => {
    if (!normalizedValue) {
        return false;
    }
    if (aliasPatterns.some((pattern) => pattern.test(normalizedValue))) {
        return true;
    }
    return textPatterns.some((text) => normalizedValue.includes(text));
};

const getQuestionSearchText = (question) => {
    const alias = normalizeText(question?.alias ?? "");
    let text = "";
    try {
        const texts = question?.texts;
        if (Array.isArray(texts)) {
            const es = texts.find((entry) => entry?.language === "es")?.text;
            text = normalizeText(es ?? texts[0]?.text ?? "");
        } else if (typeof question?.label === "string") {
            text = normalizeText(question.label);
        }
    } catch (error) {
        text = "";
    }
    return `${alias} ${text}`.trim();
};

export const findRelationshipQuestion = (questions) => {
    try {
        const list = normalizeCollection(questions);
        return (
            list.find((question) => {
                const searchText = getQuestionSearchText(question);
                return matchesAnyPattern(
                    searchText,
                    RELATIONSHIP_ALIAS_PATTERNS,
                    RELATIONSHIP_TEXT_PATTERNS,
                );
            }) ?? null
        );
    } catch (error) {
        return null;
    }
};

export const findAccusedQuestion = (questions) => {
    try {
        const list = normalizeCollection(questions);
        return (
            list.find((question) => {
                const searchText = getQuestionSearchText(question);
                return matchesAnyPattern(
                    searchText,
                    ACCUSED_ALIAS_PATTERNS,
                    ACCUSED_TEXT_PATTERNS,
                );
            }) ?? null
        );
    } catch (error) {
        return null;
    }
};

export const findInternalExternalQuestion = (questions) => {
    try {
        const list = normalizeCollection(questions);
        return (
            list.find((question) => {
                const options = getQuestionOptions(question);
                if (options.length === 0) {
                    return false;
                }
                const normalizedOptions = options.map((option) =>
                    normalizeText(
                        getOptionDisplayName(option, "es", 0) +
                            " " +
                            String(option?.value ?? "") +
                            " " +
                            String(option?.id ?? ""),
                    ),
                );
                const hasInternal = normalizedOptions.some((text) =>
                    /intern/.test(text),
                );
                const hasExternal = normalizedOptions.some((text) =>
                    /extern/.test(text),
                );
                return hasInternal && hasExternal;
            }) ?? null
        );
    } catch (error) {
        return null;
    }
};

// --- Anonimato (misma lógica que stats-page, autocontenida para tests) ---

const getSelectedOptionText = (answer) => {
    if (!Array.isArray(answer?.question?.options)) {
        return "";
    }
    const rawValues = [answer?.answerText, answer?.answer]
        .filter((v) => v !== undefined && v !== null && v !== "")
        .map((v) => String(v));
    const selected = answer.question.options.find(
        (option) =>
            rawValues.includes(String(option?.value ?? "")) ||
            rawValues.includes(String(option?.id ?? "")),
    );
    if (!selected) {
        return "";
    }
    if (typeof selected.text === "string") {
        return selected.text;
    }
    if (!Array.isArray(selected.text)) {
        return "";
    }
    return (
        selected.text.find((entry) => entry?.language === "es")?.text ??
        selected.text[0]?.text ??
        ""
    );
};

const getAnonymousStatusFromValue = (value) => {
    const normalized = normalizeText(value);
    if (!normalized) {
        return null;
    }
    if (
        [
            "1",
            "true",
            "yes",
            "si",
            "sim",
            "anonymous",
            "anonima",
            "anonimas",
            "anonimo",
            "anonimos",
        ].includes(normalized)
    ) {
        return true;
    }
    if (
        [
            "0",
            "false",
            "no",
            "nao",
            "not anonymous",
            "non anonymous",
            "no anonima",
            "no anonimas",
            "no anonimo",
            "no anonimos",
        ].includes(normalized)
    ) {
        return false;
    }
    return null;
};

const isAnonymousQuestion = (question) => {
    const normalizedAlias = normalizeText(question?.alias ?? "").replace(
        /[^a-z0-9_ ]+/g,
        " ",
    );
    if (ANONYMOUS_ALIAS_PATTERNS.some((p) => p.test(normalizedAlias))) {
        return true;
    }
    if (!Array.isArray(question?.texts)) {
        return false;
    }
    return question.texts.some((entry) =>
        ANONYMOUS_QUESTION_TEXTS.includes(
            normalizeText(entry?.text ?? "")
                .replace(/[^a-z0-9_ ]+/g, " ")
                .replace(/\s+/g, " ")
                .trim(),
        ),
    );
};

const getAnonymousAnswer = (communication) => {
    if (!Array.isArray(communication?.answers)) {
        return null;
    }
    return (
        communication.answers.find((answer) =>
            isAnonymousQuestion(answer?.question),
        ) ?? null
    );
};

const getAnonymousStatus = (answer) => {
    if (!answer) {
        return null;
    }
    const fromOption = getAnonymousStatusFromValue(
        getSelectedOptionText(answer),
    );
    if (fromOption !== null) {
        return fromOption;
    }
    return (
        getAnonymousStatusFromValue(answer?.answerText) ??
        getAnonymousStatusFromValue(answer?.answer)
    );
};

export const countAnonymity = (communications) => {
    const result = { anonymous: 0, nominative: 0, undetermined: 0 };
    try {
        for (const communication of normalizeCollection(communications)) {
            const status = getAnonymousStatus(
                getAnonymousAnswer(communication),
            );
            if (status === true) {
                result.anonymous += 1;
            } else if (status === false) {
                result.nominative += 1;
            } else {
                result.undetermined += 1;
            }
        }
    } catch (error) {
        // Devolver ceros antes que romper.
    }
    return result;
};

// --- Distribución genérica por pregunta ---

export const buildOptionDistribution = (
    communications,
    question,
    language = "es",
) => {
    const empty = { entries: [], answered: 0, missing: 0, total: 0 };
    try {
        const list = normalizeCollection(communications);
        if (!question) {
            return { ...empty, total: list.length, missing: list.length };
        }
        const options = getQuestionOptions(question);
        const questionIdentities = getQuestionMatchIdentities(question);
        const entries = options.map((option, index) => ({
            key: String(option?.id ?? option?.value ?? `option-${index}`),
            name: String(getOptionDisplayName(option, language, index)),
            value: 0,
            matchValues: getOptionMatchValues(option),
        }));
        let missing = 0;
        for (const communication of list) {
            if (!communication || typeof communication !== "object") {
                missing += 1;
                continue;
            }
            const answers = normalizeCollection(communication.answers);
            const matchingAnswer = answers.find((answer) => {
                const ids = getQuestionMatchIdentities(answer?.question);
                if (ids.size === 0 || questionIdentities.size === 0) {
                    return false;
                }
                return [...ids].some((id) => questionIdentities.has(id));
            });
            if (!matchingAnswer) {
                missing += 1;
                continue;
            }
            const candidates = getAnswerCandidateValues(matchingAnswer);
            const selected = entries.find((entry) =>
                [...entry.matchValues].some((value) => candidates.has(value)),
            );
            if (selected) {
                selected.value += 1;
            } else {
                missing += 1;
            }
        }
        const answered = entries.reduce((sum, entry) => sum + entry.value, 0);
        return {
            entries: entries.map(({ matchValues, ...rest }) => rest),
            answered,
            missing,
            total: list.length,
        };
    } catch (error) {
        return empty;
    }
};

// --- Clasificación interna / externa ---

const INTERNAL_KEYWORDS = [
    "emplead",
    "trabajad",
    "director",
    "jefe",
    "intern",
    "staff",
    "mutualista",
    "sanitari",
    "tecnic",
    "administrativ",
    "consultor",
    "miembro",
    "member",
    "employee",
    "worker",
    "socio",
];

const EXTERNAL_KEYWORDS = [
    "paciente",
    "proveedor",
    "supplier",
    "asesor",
    "autonomo",
    "asociada",
    "asociado",
    "extern",
    "cliente",
    "client",
    "otros",
    "other",
    "tercero",
    "third",
];

const classifyInternalExternal = (normalizedName) => {
    if (!normalizedName) {
        return "undetermined";
    }
    if (INTERNAL_KEYWORDS.some((keyword) => normalizedName.includes(keyword))) {
        return "internal";
    }
    if (EXTERNAL_KEYWORDS.some((keyword) => normalizedName.includes(keyword))) {
        return "external";
    }
    return "undetermined";
};

export const buildInternalExternal = (
    communications,
    questions,
    language = "es",
) => {
    const empty = { internal: 0, external: 0, undetermined: 0, total: 0 };
    try {
        const list = normalizeCollection(communications);
        const catalog = normalizeCollection(questions);
        if (list.length === 0) {
            return empty;
        }
        const dedicated = findInternalExternalQuestion(catalog);
        const target = dedicated ?? findRelationshipQuestion(catalog);
        if (!target) {
            return { ...empty, total: list.length, undetermined: list.length };
        }
        const distribution = buildOptionDistribution(list, target, language);
        let internal = 0;
        let external = 0;
        let undetermined = distribution.missing;
        for (const entry of distribution.entries) {
            const kind = dedicated
                ? (() => {
                      const normalized = normalizeText(entry.name);
                      if (/intern/.test(normalized)) return "internal";
                      if (/extern/.test(normalized)) return "external";
                      return "undetermined";
                  })()
                : classifyInternalExternal(normalizeText(entry.name));
            if (kind === "internal") internal += entry.value;
            else if (kind === "external") external += entry.value;
            else undetermined += entry.value;
        }
        return { internal, external, undetermined, total: list.length };
    } catch (error) {
        return empty;
    }
};

// --- Resoluciones (tags + pendiente) ---

const getTagText = (tag) => {
    if (typeof tag === "string" || typeof tag === "number") {
        return String(tag).trim();
    }
    if (tag && typeof tag === "object") {
        const text = tag.text ?? tag.name ?? tag.label ?? tag.value;
        return typeof text === "string" || typeof text === "number"
            ? String(text).trim()
            : "";
    }
    return "";
};

const hasSolveDate = (communication) => {
    const value = communication?.solveDate ?? communication?.solvedAt ?? null;
    if (typeof value === "string" && value.trim() !== "") {
        return true;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return true;
    }
    return false;
};

export const buildResolutions = (communications) => {
    const result = { entries: [], pending: 0, total: 0 };
    try {
        const list = normalizeCollection(communications);
        result.total = list.length;
        const counts = new Map();
        let pending = 0;
        for (const communication of list) {
            const tags = Array.isArray(communication?.tags)
                ? communication.tags
                : [];
            const names = tags
                .map((tag) => getTagText(tag))
                .filter((text) => text !== "");
            if (names.length === 0) {
                if (!hasSolveDate(communication)) {
                    pending += 1;
                } else {
                    pending += 1;
                }
                continue;
            }
            // Una comunicación puede tener varias etiquetas; se cuentan todas
            // y los porcentajes usan el total de comunicaciones como base.
            for (const name of names) {
                counts.set(name, (counts.get(name) ?? 0) + 1);
            }
            void hasSolveDate;
        }
        // Pendiente = sin fecha de cierre o sin etiquetas.
        pending = list.filter((communication) => {
            const tags = Array.isArray(communication?.tags)
                ? communication.tags
                : [];
            return tags.length === 0 || !hasSolveDate(communication);
        }).length;
        // Evitar doble conteo: si tiene tags pero no solveDate, ya está en entries
        // y también en pending (como en la imagen de ejemplo: 6+6+1=13).
        // Solo se resta cuando la comunicación ya aporta una etiqueta.
        result.pending = list.filter(
            (communication) =>
                !hasSolveDate(communication) ||
                (Array.isArray(communication?.tags)
                    ? communication.tags.length === 0
                    : true),
        ).length;
        void pending;
        result.entries = [...counts.entries()]
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
        return result;
    } catch (error) {
        return result;
    }
};

// --- Plazos (media date -> solveDate + desglose por prioridad) ---

const parseDate = (value) => {
    try {
        if (typeof value !== "string" || value.trim() === "") {
            return null;
        }
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    } catch (error) {
        return null;
    }
};

export const diffDays = (start, end) => {
    const startDate = parseDate(start);
    const endDate = parseDate(end);
    if (!startDate || !endDate) {
        return null;
    }
    const diff = Math.round((endDate.getTime() - startDate.getTime()) / 86400000);
    return diff >= 0 ? diff : null;
};

const normalizePriority = (value) => {
    const normalized = normalizeText(value);
    if (!normalized) {
        return "unknown";
    }
    if (/(^|[^a-z])(high|alta|alto)($|[^a-z])/.test(` ${normalized} `)) {
        return "high";
    }
    if (/(^|[^a-z])(medium|media|medio)($|[^a-z])/.test(` ${normalized} `)) {
        return "medium";
    }
    if (/(^|[^a-z])(low|baja|bajo)($|[^a-z])/.test(` ${normalized} `)) {
        return "low";
    }
    return "other";
};

export const buildResolutionTimes = (communications) => {
    const result = {
        average: null,
        resolved: 0,
        total: 0,
        byPriority: [],
    };
    try {
        const list = normalizeCollection(communications);
        result.total = list.length;
        const allDays = [];
        const groups = new Map();
        for (const communication of list) {
            const days = diffDays(
                communication?.date ?? communication?.createdAt,
                communication?.solveDate ?? communication?.solvedAt,
            );
            if (days === null) {
                continue;
            }
            allDays.push(days);
            const key = normalizePriority(
                communication?.priority ?? communication?.risk,
            );
            if (!groups.has(key)) {
                groups.set(key, {
                    key,
                    raw: communication?.priority ?? communication?.risk ?? key,
                    days: [],
                });
            }
            groups.get(key).days.push(days);
        }
        result.resolved = allDays.length;
        if (allDays.length > 0) {
            result.average = Math.round(
                allDays.reduce((sum, value) => sum + value, 0) / allDays.length,
            );
        }
        const order = { high: 0, medium: 1, low: 2, other: 3, unknown: 4 };
        result.byPriority = [...groups.values()]
            .map((group) => ({
                key: group.key,
                raw: group.raw,
                count: group.days.length,
                average: Math.round(
                    group.days.reduce((sum, value) => sum + value, 0) /
                        group.days.length,
                ),
            }))
            .sort(
                (a, b) =>
                    (order[a.key] ?? 99) - (order[b.key] ?? 99) ||
                    a.key.localeCompare(b.key),
            );
        return result;
    } catch (error) {
        return result;
    }
};

// --- Normalización por tipo ---

const normalizeByType = (byType) => {
    const normalized = { complaint: [], consultation: [] };
    try {
        if (!byType || typeof byType !== "object") {
            return normalized;
        }
        normalized.complaint = normalizeCollection(
            byType.complaint ?? byType.complaints,
        );
        normalized.consultation = normalizeCollection(
            byType.consultation ??
                byType.consultations ??
                byType.inquiry ??
                byType.inquiries ??
                byType.query ??
                byType.queries,
        );
    } catch (error) {
        // Listas vacías antes que romper.
    }
    return normalized;
};

const normalizeSummaryQuestions = (questionsByType) => {
    try {
        if (!questionsByType || typeof questionsByType !== "object") {
            return { complaint: [], consultation: [] };
        }
        return {
            complaint: normalizeCollection(
                questionsByType.complaint ??
                    questionsByType.complaints,
            ),
            consultation: normalizeCollection(
                questionsByType.consultation ??
                    questionsByType.consultations ??
                    questionsByType.inquiry ??
                    questionsByType.inquiries ??
                    questionsByType.query ??
                    questionsByType.queries,
            ),
        };
    } catch (error) {
        return { complaint: [], consultation: [] };
    }
};

const newDistributionAccumulator = (question, language) => {
    if (!question) {
        return { entries: [], missing: 0, total: 0 };
    }
    const options = getQuestionOptions(question);
    return {
        entries: options.map((option, index) => ({
            key: String(option?.id ?? option?.value ?? `option-${index}`),
            name: String(getOptionDisplayName(option, language, index)),
            value: 0,
            matchValues: getOptionMatchValues(option),
        })),
        missing: 0,
        total: 0,
    };
};

const mergeDistributionAccumulator = (target, partial) => {
    if (!partial || typeof partial !== "object") {
        return target;
    }
    target.total += Number(partial.total) || 0;
    target.missing += Number(partial.missing) || 0;
    const partialEntries = Array.isArray(partial.entries) ? partial.entries : [];
    for (const partialEntry of partialEntries) {
        const key = String(partialEntry?.key ?? "");
        const existing = target.entries.find((entry) => entry.key === key);
        if (existing) {
            existing.value += Number(partialEntry?.value) || 0;
        } else {
            target.entries.push({
                key,
                name: String(partialEntry?.name ?? key),
                value: Number(partialEntry?.value) || 0,
                matchValues: partialEntry?.matchValues ?? new Set(),
            });
        }
    }
    return target;
};

const finalizeDistributionAccumulator = (accumulator) => {
    const answered = accumulator.entries.reduce(
        (sum, entry) => sum + (Number(entry.value) || 0),
        0,
    );
    return {
        entries: accumulator.entries.map(({ matchValues, ...rest }) => ({
            ...rest,
        })),
        answered,
        missing: accumulator.missing,
        total: accumulator.total,
    };
};

const newSummaryTypeAccumulator = (catalog, language) => ({
    language: typeof language === "string" ? language : "es",
    total: 0,
    anonymity: { anonymous: 0, nominative: 0, undetermined: 0 },
    relationshipQuestion: findRelationshipQuestion(catalog),
    accusedQuestion: findAccusedQuestion(catalog),
    internalExternalDedicated: Boolean(findInternalExternalQuestion(catalog)),
    internalExternalTarget:
        findInternalExternalQuestion(catalog) ??
        findRelationshipQuestion(catalog),
    internalExternal: newDistributionAccumulator(
        findInternalExternalQuestion(catalog) ??
            findRelationshipQuestion(catalog),
        language,
    ),
    informants: newDistributionAccumulator(
        findRelationshipQuestion(catalog),
        language,
    ),
    accused: newDistributionAccumulator(
        findAccusedQuestion(catalog),
        language,
    ),
    tagCounts: new Map(),
    pending: 0,
    timeSum: 0,
    timeResolved: 0,
    timeByPriority: new Map(),
});

const feedSummaryTypeAccumulator = (accumulator, communications) => {
    const list = normalizeCollection(communications);
    if (list.length === 0) {
        return accumulator;
    }
    accumulator.total += list.length;
    const anonymity = countAnonymity(list);
    accumulator.anonymity.anonymous += anonymity.anonymous;
    accumulator.anonymity.nominative += anonymity.nominative;
    accumulator.anonymity.undetermined += anonymity.undetermined;
    mergeDistributionAccumulator(
        accumulator.internalExternal,
        buildOptionDistribution(
            list,
            accumulator.internalExternalTarget,
            accumulator.language,
        ),
    );
    mergeDistributionAccumulator(
        accumulator.informants,
        buildOptionDistribution(
            list,
            accumulator.relationshipQuestion,
            accumulator.language,
        ),
    );
    mergeDistributionAccumulator(
        accumulator.accused,
        buildOptionDistribution(list, accumulator.accusedQuestion, accumulator.language),
    );
    const resolutions = buildResolutions(list);
    for (const entry of resolutions.entries) {
        accumulator.tagCounts.set(
            entry.name,
            (accumulator.tagCounts.get(entry.name) ?? 0) + entry.value,
        );
    }
    accumulator.pending += resolutions.pending;
    const times = buildResolutionTimes(list);
    accumulator.timeResolved += times.resolved;
    accumulator.timeSum +=
        times.average === null || times.average === undefined
            ? 0
            : times.average * times.resolved;
    for (const group of times.byPriority) {
        const existing = accumulator.timeByPriority.get(group.key);
        if (existing) {
            existing.sum += group.average * group.count;
            existing.count += group.count;
        } else {
            accumulator.timeByPriority.set(group.key, {
                key: group.key,
                raw: group.raw,
                sum: group.average * group.count,
                count: group.count,
            });
        }
    }
    return accumulator;
};

/**
 * Acumulador incremental del resumen: alimenta lotes acotados de
 * comunicaciones (p. ej. páginas de 100) y libera cada lote tras contar,
 * sin reconstruir el universo completo en el navegador. build() devuelve
 * exactamente la misma forma que buildStatsSummary.
 */
export const createStatsSummaryAccumulator = ({
    questionsByType,
    language = "es",
} = {}) => {
    const questions = normalizeSummaryQuestions(questionsByType);
    const safeLanguage = typeof language === "string" ? language : "es";
    const state = {
        complaint: newSummaryTypeAccumulator(
            questions.complaint,
            safeLanguage,
        ),
        consultation: newSummaryTypeAccumulator(
            questions.consultation,
            safeLanguage,
        ),
    };
    const feed = (communications, communicationType) => {
        const key =
            communicationType === "consultation"
                ? "consultation"
                : "complaint";
        feedSummaryTypeAccumulator(state[key], communications);
    };
    const buildForType = (typeAccumulator) => {
        const order = { high: 0, medium: 1, low: 2, other: 3, unknown: 4 };
        return {
            total: typeAccumulator.total,
            anonymity: { ...typeAccumulator.anonymity },
            internalExternal: (() => {
                const distribution = finalizeDistributionAccumulator(
                    typeAccumulator.internalExternal,
                );
                let internal = 0;
                let external = 0;
                let undetermined = distribution.missing;
                for (const entry of typeAccumulator.internalExternal.entries) {
                    const kind = typeAccumulator.internalExternalDedicated
                        ? (() => {
                              const normalized = normalizeText(entry.name);
                              if (/intern/.test(normalized)) return "internal";
                              if (/extern/.test(normalized)) return "external";
                              return "undetermined";
                          })()
                        : classifyInternalExternal(normalizeText(entry.name));
                    if (kind === "internal") internal += entry.value;
                    else if (kind === "external") external += entry.value;
                    else undetermined += entry.value;
                }
                return {
                    internal,
                    external,
                    undetermined,
                    total: typeAccumulator.total,
                };
            })(),
            informants: {
                ...finalizeDistributionAccumulator(typeAccumulator.informants),
                questionFound: Boolean(typeAccumulator.relationshipQuestion),
            },
            accused: {
                ...finalizeDistributionAccumulator(typeAccumulator.accused),
                questionFound: Boolean(typeAccumulator.accusedQuestion),
                questionName: typeAccumulator.accusedQuestion
                    ? String(
                          typeAccumulator.accusedQuestion.alias ??
                              typeAccumulator.accusedQuestion.id ??
                              "",
                      )
                    : "",
            },
            resolutions: {
                entries: [...typeAccumulator.tagCounts.entries()]
                    .map(([name, value]) => ({ name, value }))
                    .sort(
                        (a, b) =>
                            b.value - a.value || a.name.localeCompare(b.name),
                    ),
                pending: typeAccumulator.pending,
                total: typeAccumulator.total,
            },
            times: {
                average:
                    typeAccumulator.timeResolved > 0
                        ? Math.round(
                              typeAccumulator.timeSum /
                                  typeAccumulator.timeResolved,
                          )
                        : null,
                resolved: typeAccumulator.timeResolved,
                total: typeAccumulator.total,
                byPriority: [...typeAccumulator.timeByPriority.values()]
                    .map((group) => ({
                        key: group.key,
                        raw: group.raw,
                        count: group.count,
                        average: Math.round(group.sum / group.count),
                    }))
                    .sort(
                        (a, b) =>
                            (order[a.key] ?? 99) - (order[b.key] ?? 99) ||
                            a.key.localeCompare(b.key),
                    ),
            },
        };
    };
    return {
        feed,
        build() {
            return {
                complaint: buildForType(state.complaint),
                consultation: buildForType(state.consultation),
            };
        },
    };
};

export const buildStatsSummary = ({
    communicationsByType,
    questionsByType,
    language = "es",
} = {}) => {
    try {
        const communications = normalizeByType(communicationsByType);
        const accumulator = createStatsSummaryAccumulator({
            questionsByType,
            language,
        });
        accumulator.feed(communications.complaint, "complaint");
        accumulator.feed(communications.consultation, "consultation");
        return accumulator.build();
    } catch (error) {
        return {
            complaint: {
                total: 0,
                anonymity: { anonymous: 0, nominative: 0, undetermined: 0 },
                internalExternal: {
                    internal: 0,
                    external: 0,
                    undetermined: 0,
                    total: 0,
                },
                informants: {
                    entries: [],
                    answered: 0,
                    missing: 0,
                    total: 0,
                    questionFound: false,
                },
                accused: {
                    entries: [],
                    answered: 0,
                    missing: 0,
                    total: 0,
                    questionFound: false,
                    questionName: "",
                },
                resolutions: { entries: [], pending: 0, total: 0 },
                times: { average: null, resolved: 0, total: 0, byPriority: [] },
            },
            consultation: {
                total: 0,
                anonymity: { anonymous: 0, nominative: 0, undetermined: 0 },
                internalExternal: {
                    internal: 0,
                    external: 0,
                    undetermined: 0,
                    total: 0,
                },
                informants: {
                    entries: [],
                    answered: 0,
                    missing: 0,
                    total: 0,
                    questionFound: false,
                },
                accused: {
                    entries: [],
                    answered: 0,
                    missing: 0,
                    total: 0,
                    questionFound: false,
                    questionName: "",
                },
                resolutions: { entries: [], pending: 0, total: 0 },
                times: { average: null, resolved: 0, total: 0, byPriority: [] },
            },
        };
    }
};

export const formatCountPercent = (count, total) => {
    const safeCount = Number(count) || 0;
    const safeTotal = Number(total) || 0;
    if (safeCount <= 0 || safeTotal <= 0) {
        return "—";
    }
    const percent = Math.round((safeCount / safeTotal) * 100);
    return `${safeCount} (${percent}%)`;
};

export const formatEntryLine = (count, name, total) => {
    const safeCount = Number(count) || 0;
    const safeTotal = Number(total) || 0;
    if (safeCount <= 0 || safeTotal <= 0) {
        return null;
    }
    const percent = Math.round((safeCount / safeTotal) * 100);
    return `${safeCount} ${name} (${percent}%)`;
};

const CellList = ({ lines }) => {
    const safeLines = Array.isArray(lines)
        ? lines.filter((line) => typeof line === "string" && line.trim() !== "")
        : [];
    if (safeLines.length === 0) {
        return <span className="text-muted-foreground">—</span>;
    }
    return (
        <ul className="space-y-1">
            {safeLines.map((line, index) => (
                <li key={`${index}:${line}`}>{line}</li>
            ))}
        </ul>
    );
};

const priorityLabelKey = (key) => {
    if (key === "high") return "priorities.high";
    if (key === "medium") return "priorities.medium";
    if (key === "low") return "priorities.low";
    return null;
};

const StatsSummaryTable = ({
    communicationsByType,
    questionsByType,
    language,
    summary: prebuiltSummary,
}) => {
    const { t, i18n } = useTranslation();
    const activeLanguage = language ?? i18n.language ?? "es";

    const summary = useMemo(() => {
        if (prebuiltSummary && typeof prebuiltSummary === "object") {
            return prebuiltSummary;
        }
        try {
            return buildStatsSummary({
                communicationsByType,
                questionsByType,
                language: activeLanguage,
            });
        } catch (error) {
            return buildStatsSummary({
                communicationsByType: { complaint: [], consultation: [] },
                questionsByType: { complaint: [], consultation: [] },
                language: activeLanguage,
            });
        }
    }, [prebuiltSummary, communicationsByType, questionsByType, activeLanguage]);

    const daysWord = t("general.days", { defaultValue: "días" });
    const pendingWord = t("statsPage.summary.pending", {
        defaultValue: "pendiente",
    });

    const renderAnonymity = (data) => {
        const total = data.total;
        if (!total) {
            return <span className="text-muted-foreground">—</span>;
        }
        const parts = [
            formatCountPercent(data.anonymity.anonymous, total),
            formatCountPercent(data.anonymity.nominative, total),
        ];
        if (data.anonymity.undetermined > 0) {
            parts.push(
                formatCountPercent(data.anonymity.undetermined, total),
            );
        }
        return parts.join(" / ");
    };

    const renderInternalExternal = (data) => {
        const total = data.total;
        if (!total) {
            return <span className="text-muted-foreground">—</span>;
        }
        return [
            formatCountPercent(data.internalExternal.internal, total),
            formatCountPercent(data.internalExternal.external, total),
            formatCountPercent(data.internalExternal.undetermined, total),
        ].join(" / ");
    };

    const renderDistribution = (distribution, total) => {
        if (!distribution?.questionFound) {
            return <span className="text-muted-foreground">—</span>;
        }
        const lines = (distribution.entries ?? [])
            .filter((entry) => Number(entry?.value) > 0)
            .map((entry) =>
                formatEntryLine(entry.value, entry.name, total),
            )
            .filter(Boolean);
        if (distribution.missing > 0 && distribution.answered > 0) {
            const missingLine = formatEntryLine(
                distribution.missing,
                t("statsPage.summary.undetermined", {
                    defaultValue: "No determinado",
                }),
                total,
            );
            if (missingLine) {
                lines.push(missingLine);
            }
        }
        return <CellList lines={lines} />;
    };

    const renderResolutions = (data) => {
        const total = data.total;
        if (!total) {
            return <span className="text-muted-foreground">—</span>;
        }
        const lines = (data.resolutions.entries ?? [])
            .filter((entry) => Number(entry?.value) > 0)
            .map((entry) =>
                formatEntryLine(entry.value, entry.name, total),
            )
            .filter(Boolean);
        if (data.resolutions.pending > 0) {
            const pendingLine = formatEntryLine(
                data.resolutions.pending,
                pendingWord,
                total,
            );
            if (pendingLine) {
                lines.push(pendingLine);
            }
        }
        return <CellList lines={lines} />;
    };

    const renderTimes = (data) => {
        if (!data.total || data.times.resolved === 0 || data.times.average === null) {
            return <span className="text-muted-foreground">—</span>;
        }
        const lines = [`${data.times.average} ${daysWord}`];
        const groups = Array.isArray(data.times.byPriority)
            ? data.times.byPriority.filter((group) => group.count > 0)
            : [];
        const showBreakdown = groups.length > 1;
        if (showBreakdown) {
            for (const group of groups) {
                const labelKey = priorityLabelKey(group.key);
                const label = labelKey
                    ? t(labelKey, { defaultValue: group.key })
                    : String(group.raw ?? group.key);
                lines.push(
                    `${group.count} ${label}: ${group.average} ${daysWord}`,
                );
            }
        }
        return <CellList lines={lines} />;
    };

    const hasData =
        summary.complaint.total > 0 || summary.consultation.total > 0;

    return (
        <section
            className="my-4"
            data-testid="stats-summary-table"
            data-has-data={hasData ? "true" : "false"}
            data-complaints={summary.complaint.total}
            data-consultations={summary.consultation.total}
        >
            <Card>
                <CardHeader>
                    <CardTitle>{t("statsPage.summary.title")}</CardTitle>
                    <CardDescription>
                        {t("statsPage.summary.description")}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] border-collapse text-sm">
                            <caption className="sr-only">
                                {t("statsPage.summary.title")}
                            </caption>
                            <thead>
                                <tr className="border-b border-slate-200 bg-muted/50 text-left">
                                    <th
                                        scope="col"
                                        className="px-4 py-3 font-semibold"
                                    >
                                        {t("statsPage.summary.mainData")}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-4 py-3 font-semibold"
                                        data-testid="stats-summary-header-complaint"
                                    >
                                        {`${summary.complaint.total} ${t("communicationTypes.complaint")}`}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-4 py-3 font-semibold"
                                        data-testid="stats-summary-header-consultation"
                                    >
                                        {`${summary.consultation.total} ${t("communicationTypes.consultation")}`}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b border-slate-100 align-top">
                                    <th
                                        scope="row"
                                        className="px-4 py-3 text-left font-medium"
                                    >
                                        {t("statsPage.summary.anonymity")}
                                    </th>
                                    <td className="px-4 py-3">
                                        {renderAnonymity(summary.complaint)}
                                    </td>
                                    <td className="px-4 py-3">
                                        {renderAnonymity(summary.consultation)}
                                    </td>
                                </tr>
                                <tr className="border-b border-slate-100 align-top">
                                    <th
                                        scope="row"
                                        className="px-4 py-3 text-left font-medium"
                                    >
                                        {t("statsPage.summary.internalExternal")}
                                    </th>
                                    <td className="px-4 py-3">
                                        {renderInternalExternal(summary.complaint)}
                                    </td>
                                    <td className="px-4 py-3">
                                        {renderInternalExternal(summary.consultation)}
                                    </td>
                                </tr>
                                <tr className="border-b border-slate-100 align-top">
                                    <th
                                        scope="row"
                                        className="px-4 py-3 text-left font-medium"
                                    >
                                        {t("statsPage.summary.informantGroups")}
                                    </th>
                                    <td className="px-4 py-3">
                                        {renderDistribution(
                                            summary.complaint.informants,
                                            summary.complaint.total,
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {renderDistribution(
                                            summary.consultation.informants,
                                            summary.consultation.total,
                                        )}
                                    </td>
                                </tr>
                                <tr className="border-b border-slate-100 align-top">
                                    <th
                                        scope="row"
                                        className="px-4 py-3 text-left font-medium"
                                    >
                                        {t("statsPage.summary.accusedGroups")}
                                    </th>
                                    <td className="px-4 py-3">
                                        {renderDistribution(
                                            summary.complaint.accused,
                                            summary.complaint.total,
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {renderDistribution(
                                            summary.consultation.accused,
                                            summary.consultation.total,
                                        )}
                                    </td>
                                </tr>
                                <tr className="border-b border-slate-100 align-top">
                                    <th
                                        scope="row"
                                        className="px-4 py-3 text-left font-medium"
                                    >
                                        {t("statsPage.summary.resolutions")}
                                    </th>
                                    <td className="px-4 py-3">
                                        {renderResolutions(summary.complaint)}
                                    </td>
                                    <td className="px-4 py-3">
                                        {renderResolutions(summary.consultation)}
                                    </td>
                                </tr>
                                <tr className="align-top">
                                    <th
                                        scope="row"
                                        className="px-4 py-3 text-left font-medium"
                                    >
                                        {t("statsPage.summary.resolutionTimes")}
                                    </th>
                                    <td className="px-4 py-3">
                                        {renderTimes(summary.complaint)}
                                    </td>
                                    <td className="px-4 py-3">
                                        {renderTimes(summary.consultation)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </section>
    );
};

export default StatsSummaryTable;
